import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { TENANT_KEY } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, ShieldAlert, LogIn, AlertTriangle } from "lucide-react";

const quando = (s) =>
  s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

const STATI = {
  pending: ["In attesa della sua risposta", "bg-amber-500/15 text-amber-300"],
  approved: ["Autorizzato", "bg-emerald-500/15 text-emerald-400"],
  emergency: ["Emergenza", "bg-rose-500/15 text-rose-400"],
  denied: ["Rifiutato", "bg-slate-700 text-slate-300"],
  expired: ["Decaduto", "bg-slate-700 text-slate-300"],
  closed: ["Chiuso", "bg-slate-700 text-slate-300"],
};

/** Console: chiedi al titolare il permesso di entrare nel suo gestionale (o entra in emergenza). */
export const ConsoleAccessi = ({ client, slug }) => {
  const [d, setD] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!slug) return;
    client.get(`/admin/access?tenant=${slug}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [client, slug]);

  useEffect(() => {
    load();
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, [load]);

  if (!slug) return null;

  const chiedi = async (via) => {
    if (!motivo.trim()) return toast.error("Scrivi il motivo: il titolare lo vede");
    setBusy(true);
    try {
      await client.post(`/admin/access/${via}`, { tenant: slug, reason: motivo.trim() });
      toast.success(via === "emergency"
        ? "Accesso d'emergenza aperto: al titolare arriva l'avviso"
        : "Richiesta inviata al titolare");
      setMotivo("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Richiesta non riuscita");
    } finally {
      setBusy(false);
    }
  };

  const entra = async (id) => {
    try {
      const r = await client.post(`/admin/access/${id}/enter`, { tenant: slug });
      localStorage.setItem(TENANT_KEY, r.data.token);
      localStorage.removeItem("rh_user");
      window.open("/", "_blank");
      toast.success(`Sessione aperta per ${r.data.minutes} minuti`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accesso non disponibile");
    }
  };

  const righe = d ? [...d.pending, ...d.storico].slice(0, 8) : [];

  return (
    <div className="rounded-lg border border-slate-800 p-3" data-testid="console-accessi">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
        <ShieldCheck className="h-3.5 w-3.5" /> Accesso al gestionale
      </p>
      <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">
        Non entri quando vuoi: chiedi il permesso indicando il motivo, il titolare autorizza e la sessione
        si chiude da sola. L'emergenza resta segnata in rosso nel suo registro e gli manda un'email.
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        <Input
          className="h-10 min-w-[200px] flex-1 border-slate-700 bg-slate-950 text-slate-100"
          placeholder="Motivo (es. la cassa fiscale non stampa)"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          data-testid="console-accesso-motivo"
        />
        <Button size="sm" className="h-10" disabled={busy} onClick={() => chiedi("request")} data-testid="console-accesso-chiedi">
          Chiedi accesso
        </Button>
        <Button size="sm" variant="outline" className="h-10 border-rose-500/40 text-rose-400 hover:bg-rose-500/10"
                disabled={busy} onClick={() => chiedi("emergency")} data-testid="console-accesso-emergenza">
          <AlertTriangle className="mr-1 h-3.5 w-3.5" /> Emergenza
        </Button>
      </div>

      <div className="mt-3 space-y-2">
        {righe.length === 0 && <p className="text-[11px] text-slate-500">Nessun accesso registrato.</p>}
        {righe.map((r) => {
          const [testo, stile] = STATI[r.status] || ["Accesso", "bg-slate-700 text-slate-300"];
          return (
            <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-md bg-slate-900/60 px-3 py-2" data-testid={`console-accesso-${r.id}`}>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${stile}`}>{testo}</span>
              <span className="min-w-0 flex-1 truncate text-[11px] text-slate-400">
                {r.reason} · {quando(r.at)}
              </span>
              {(r.status === "approved" || r.status === "emergency") && (
                <Button size="sm" variant="ghost" className="h-7 text-sky-400" onClick={() => entra(r.id)} data-testid={`console-accesso-entra-${r.id}`}>
                  <LogIn className="mr-1 h-3.5 w-3.5" /> Entra
                </Button>
              )}
              {r.status === "pending" && <ShieldAlert className="h-3.5 w-3.5 text-amber-400" />}
            </div>
          );
        })}
      </div>
    </div>
  );
};
